<template>
  <b-form
    id="privilege-image"
    @submit.prevent="onSubmitForm"
  >
    <b-row>
      <b-col cols="12">
        <b-card
          no-body
          class="mb-1"
        >
          <b-card-header>
            <span class="information-header">{{ 'Main Image' }}</span>
          </b-card-header>
          <div class="px-2">
            <div class="row">
              <div class="col-12">
                <div class="m-4">
                  <image-uploader
                    :image-url="formData.imageMaster.url"
                    :removable="true"
                    @onUpload="onUploadImage"
                    @onDelete="onDeleteImage"
                  />
                </div>
              </div>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>
    <b-row>
      <b-col cols="12">
        <b-card
          no-body
          class="px-2 pb-4 mb-1"
        >
          <b-card-header>
            <span class="information-header">{{ 'Manage Image' }}</span>
          </b-card-header>
          <b-row>
            <b-col
              cols="12"
              class="mb-2"
            >
              <div class="d-flex justify-content-end mt-2 mb-1">
                <add-button
                  :btn-label="'Add'"
                  @click.native="openManageImageListModal"
                />
              </div>
            </b-col>
            <b-col cols="12">
              <overlay-loading :loading="loading.tableData">
                <template #content>
                  <b-table
                    :key="tableModeReorder + keyChange"
                    v-sortable-row="sortableRowOptions"
                    :fields="fields"
                    :items="ImageListTable.payload"
                    responsive
                    bordered
                    borderless
                    sticky-header
                    show-empty
                    no-local-sorting
                    class="horoscope-table"
                    :class="
                      !sortableRowOptions.disabled ? 'table-reorder' : null
                    "
                  >
                    <template #cell(type)="data">
                      <div>
                        {{ data.item.type == 1 ? 'Single' : 'Multiple' }}
                      </div>
                    </template>
                    <template #cell(image_url)="data">
                      <img
                        v-if="data.item.image_url"
                        :src="data.item.image_url"
                        class="horoscope-image-column"
                      >
                      <div
                        v-else
                        class="text-center"
                      >
                        -
                      </div>
                    </template>
                    <template #cell(latest_update)="data">
                      <div>
                        {{
                          data.item.latest_update
                            ? resolveDate(data.item.latest_update) +
                              ' ' +
                              resolveTime(data.item.latest_update)
                            : '-'
                        }}
                      </div>
                    </template>

                    <template #cell(actions)="data">
                      <action-group
                        :resolve-action="resolveMappingTableAction"
                        :set-action-list="mappingActionList"
                        :item-data="data"
                      />
                    </template>
                    <template #table-busy>
                      <overlay-loading :loading="true" />
                    </template>
                    <template #empty>
                      <card-no-item :text-no-img="'Item Not Found'" />
                    </template>
                  </b-table>
                </template>
              </overlay-loading>
            </b-col>
            <b-col cols="12">
              <pagination-with-reorder
                entity-editable
                reordering
                :pagination="pagination"
                @reorder="onClickReorder"
                @entityChange="onFilterEntity"
                @pageChange="onPageChange"
              />
            </b-col>
          </b-row>
        </b-card>
      </b-col>
    </b-row>
    <reorder-modal
      v-if="modalType.reorder"
      :current-index="currentIndex"
      :reorder-action="reorderImageList"
      @onCloseModal="closeReorderModal"
    />

    <manage-image-list-modal
      v-if="modalType.manageImageList"
      :is-edit="isEditImage"
      :image-obj="imageDetailItem"
      @onSubmit="saveImageList"
      @onCloseModal="closeManageImageListModal"
    />
    <div class="privilege-btn-wrp mt-2 mb-2">
      <div class="d-flex justify-content-center">
        <cancel-button
          class="mr-1"
          :btn-label="'Back'"
          @click.native="onBack"
          stageBack
        />
        <save-button
          :type="'submit'"
          class="ml-1"
          :btn-label="'Next'"
        />
      </div>
    </div>
  </b-form>
</template>

<script>
import PaginationWithReorder from '@/views/components/pagination/PaginationWithReorder.vue'
import Sortable from 'sortablejs'
import ActionGroup from '@/views/components/table-action-group/ActionGroup.vue'
import ReorderModal from '@/@core/components/reorder-modal/ReorderModal.vue'
import CardNoItem from '@core/components/card-no-item/CardNoItem.vue'
import usePrivilegeImage from '../hooks/usePrivilegeImage'
import ManageImageListModal from './modal/ManageImageListModal.vue'

const createSortable = (el, options, vnode) => Sortable.create(el, {
  ...options,
})

const sortableRow = {
  name: 'sortableRow',
  bind(el, binding, vnode) {
    const table = el
    table._sortable = createSortable(
      table.querySelector('tbody'),
      binding.value,
      vnode,
    )
  },
}

export default {
  directives: {
    sortableRow,
  },
  components: {
    PaginationWithReorder,
    ActionGroup,
    ReorderModal,
    ManageImageListModal,
    CardNoItem,
  },
  props: {
    isEdit: { type: Boolean, default: false },
    privilegeId: { type: Number, default: 0 },
  },
  setup(props) {
    const {
      loading,
      pagination,
      onPageChange,
      onFilterEntity,
      formData,
      fields,
      ImageListTable,
      sortableRowOptions,
      keyChange,
      mappingActionList,
      resolveMappingTableAction,
      currentIndex,
      isEditImage,
      reorderImageList,
      onUploadImage,
      onDeleteImage,
      onClickReorder,
      closeModal,
      tableModeReorder,
      openManageImageListModal,
      closeManageImageListModal,
      closeReorderModal,
      modalType,
      saveImageList,
      onSubmitForm,
      resolveDate,
      resolveTime,
      resolveDateString,
      resolveDateObject,
      imageDetailItem,
      onBack,
    } = usePrivilegeImage(props)

    return {
      loading,
      pagination,
      onPageChange,
      onFilterEntity,
      formData,
      fields,
      ImageListTable,
      sortableRowOptions,
      keyChange,
      mappingActionList,
      resolveMappingTableAction,
      currentIndex,
      isEditImage,
      reorderImageList,
      onUploadImage,
      onDeleteImage,
      onClickReorder,
      closeModal,
      tableModeReorder,
      openManageImageListModal,
      closeManageImageListModal,
      closeReorderModal,
      modalType,
      saveImageList,
      onSubmitForm,
      resolveDate,
      resolveTime,
      resolveDateString,
      resolveDateObject,
      imageDetailItem,
      onBack,
    }
  },
}
</script>
